import type { ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { LineChart } from '../shared/charts'
import { useAdminOverview } from './admin-overview-provider'
import { deriveCompletionSeries } from './derive'

const Y_AXIS_LABELS = [100, 75, 50, 25, 0]

export function CompletionChart(): ReactNode {
  const { overview, isLoading } = useAdminOverview()
  const cohorts = overview?.completionByTrack ?? []
  const { weekNumbers, series } = deriveCompletionSeries(cohorts)
  const xLabels = weekNumbers.map((week) => `S${week}`)

  return (
    <Card className="h-full gap-4 border-(--cd-border) bg-(--cd-card) py-4 text-(--cd-text) shadow-none">
      <CardHeader className="gap-1 px-4">
        <CardTitle className="text-sm">Homework completion by track</CardTitle>
        <CardDescription className="text-xs text-(--cd-text-muted)">
          Percent of assigned tasks finished each sprint
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4">
        {isLoading ? (
          <p className="py-8 text-center text-xs text-(--cd-text-faint)">
            Loading completion data…
          </p>
        ) : series.length === 0 ? (
          <p className="py-8 text-center text-xs text-(--cd-text-faint)">
            No completion data yet — it appears once homework is scheduled.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-2">
              <div
                role="group"
                aria-label="Completion percentage axis"
                className="flex h-[180px] flex-col justify-between text-[10px] text-(--cd-text-faint)"
              >
                {Y_AXIS_LABELS.map((percent) => (
                  <span key={percent}>{percent}%</span>
                ))}
              </div>
              <LineChart
                series={series.map(({ label, color, points }) => ({
                  label,
                  color,
                  points,
                }))}
                xLabels={xLabels}
                height={180}
              />
            </div>
            <ul
              aria-label="Completion series legend"
              className="flex flex-wrap items-center gap-x-4 gap-y-2"
            >
              {series.map((item) => (
                <li
                  key={item.track}
                  className="flex items-center gap-1.5 text-xs text-(--cd-text-2)"
                >
                  <span
                    aria-hidden="true"
                    className="size-2 rounded-full"
                    style={{ background: item.color }}
                  />
                  {item.label}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  )
}
